import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { supabase, isSupabaseConfigured } from "../lib/supabase";
import {
  listBookings,
  addBlock,
  deleteBlock,
  getBlockedDates,
  updateBookingStatus,
} from "../lib/availability";

const fmt = (d) =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`
    : "";

// Parse "YYYY-MM-DD" as a local date (no UTC shift)
const parseLocalDate = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(y, m - 1, d);
};

const Wrap = ({ children }) => (
  <section className="min-h-screen bg-cream pt-28 md:pt-32 pb-24">
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
      <title>Admin — Laurier Rose</title>
    </Helmet>
    <div className="max-w-6xl mx-auto px-5 md:px-10">{children}</div>
  </section>
);

const Stat = ({ label, value, accent }) => (
  <div className="bg-cream-dark/50 px-6 py-5">
    <div className="eyebrow text-ink-muted text-[10px] mb-2">{label}</div>
    <div className={`font-serif text-4xl font-light ${accent || "text-ink"}`}>
      {value}
    </div>
  </div>
);

export default function Admin() {
  const [session, setSession] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [range, setRange] = useState();
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) =>
      setSession(s)
    );
    return () => sub.subscription.unsubscribe();
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await listBookings();
    setBookings(data || []);
    setBlocks(await getBlockedDates());
  }, []);

  useEffect(() => {
    if (session) refresh();
  }, [session, refresh]);

  const signIn = async (e) => {
    e.preventDefault();
    if (signingIn) return;
    setAuthError("");
    setSigningIn(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) setAuthError("Invalid email or password.");
    setSigningIn(false);
  };

  const doBlock = async () => {
    if (!range?.from || !range?.to) return;
    setBusy(true);
    await addBlock({
      start_date: fmt(range.from),
      end_date: fmt(range.to),
      reason: "Manual block",
    });
    setRange(undefined);
    await refresh();
    setBusy(false);
  };

  const confirmBooking = async (b) => {
    await updateBookingStatus(b.id, { status: "confirmed" });
    if (b.check_in && b.check_out) {
      await addBlock({
        room_id: b.room_id,
        start_date: b.check_in,
        end_date: b.check_out,
        reason: `Booking: ${b.guest_name || ""}`,
      });
    }
    refresh();
  };

  if (!isSupabaseConfigured) {
    return (
      <Wrap>
        <h1 className="font-serif text-3xl text-ink mb-4">Admin</h1>
        <p className="text-ink-soft">
          Supabase isn't configured yet. Add <code>REACT_APP_SUPABASE_URL</code>{" "}
          and <code>REACT_APP_SUPABASE_ANON_KEY</code> to <code>.env</code> (and
          Vercel), run <code>supabase-schema.sql</code>, then restart.
        </p>
      </Wrap>
    );
  }

  if (!session) {
    return (
      <Wrap>
        <div className="max-w-sm mx-auto">
          <h1 className="font-serif text-3xl text-ink mb-2 text-center">
            Laurier Rose · Admin
          </h1>
          <div className="rule-gold mx-auto mb-8" />
          <form onSubmit={signIn} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              autoCapitalize="none"
              spellCheck="false"
              className="w-full px-4 py-3 border border-ink/15 bg-white focus:ring-2 focus:ring-brass focus:border-transparent"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full px-4 py-3 border border-ink/15 bg-white focus:ring-2 focus:ring-brass focus:border-transparent"
            />
            {authError && <p className="text-red-600 text-sm">{authError}</p>}
            <button
              type="submit"
              disabled={signingIn}
              className="w-full bg-ink hover:bg-brass disabled:opacity-50 text-cream py-3 text-[12px] uppercase tracking-[0.22em] transition-colors"
            >
              {signingIn ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </Wrap>
    );
  }

  const blockedRanges = blocks.map((b) => ({
    from: parseLocalDate(b.start_date),
    to: parseLocalDate(b.end_date),
  }));
  const pending = bookings.filter((b) => b.status === "pending").length;
  const confirmed = bookings.filter((b) => b.status === "confirmed").length;
  const filtered =
    filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const tabs = ["all", "pending", "confirmed", "cancelled"];

  return (
    <Wrap>
      {/* Header */}
      <div className="flex items-center justify-between mb-10 pb-6 border-b border-ink/10">
        <div>
          <span className="font-serif text-2xl text-ink">
            Laurier <span className="text-brass">Rose</span>
          </span>
          <span className="eyebrow text-ink-muted ml-3 text-[10px]">Admin</span>
        </div>
        <button
          onClick={() => supabase.auth.signOut()}
          className="text-[12px] uppercase tracking-[0.18em] text-ink-muted hover:text-brass"
        >
          Sign out
        </button>
      </div>

      {/* Account / session */}
      <div className="bg-cream-dark/50 px-6 py-5 mb-8">
        <div className="eyebrow text-ink-muted text-[10px] mb-3">Signed in</div>
        <div className="grid sm:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="block text-xs text-ink-muted">Email</span>
            <span className="text-ink break-all">{session.user?.email}</span>
          </div>
          <div>
            <span className="block text-xs text-ink-muted">Last sign-in</span>
            <span className="text-ink">
              {session.user?.last_sign_in_at
                ? new Date(session.user.last_sign_in_at).toLocaleString()
                : "—"}
            </span>
          </div>
          <div>
            <span className="block text-xs text-ink-muted">Account ID</span>
            <span className="text-ink break-all">{session.user?.id}</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 md:gap-4 mb-12">
        <Stat label="Pending requests" value={pending} accent="text-brass" />
        <Stat label="Confirmed" value={confirmed} accent="text-olive" />
        <Stat label="Blocked periods" value={blocks.length} />
      </div>

      <div className="grid lg:grid-cols-2 gap-12 md:gap-16">
        {/* Block dates */}
        <div>
          <h2 className="font-serif text-2xl text-ink mb-1">Block dates</h2>
          <p className="text-ink-muted text-sm mb-4">
            Crossed-out days are already blocked.
          </p>
          <div className="border border-ink/10 bg-white p-4 inline-block">
            <DayPicker
              mode="range"
              weekStartsOn={1}
              selected={range}
              onSelect={setRange}
              disabled={{ before: new Date() }}
              modifiers={{ blocked: blockedRanges }}
              modifiersClassNames={{ blocked: "day-blocked" }}
            />
          </div>
          <button
            onClick={doBlock}
            disabled={busy || !range?.from || !range?.to}
            className="mt-4 block bg-ink hover:bg-brass disabled:opacity-40 text-cream px-6 py-3 text-[12px] uppercase tracking-[0.2em] transition-colors"
          >
            Block selected dates
          </button>

          <ul className="mt-6 space-y-2">
            {blocks.map((b) => (
              <li
                key={b.id}
                className="flex items-center justify-between text-sm border-b border-ink/10 py-2"
              >
                <span className="text-ink-soft">
                  {b.start_date} → {b.end_date}
                  {b.reason ? ` · ${b.reason}` : ""}
                </span>
                <button
                  onClick={() => deleteBlock(b.id).then(refresh)}
                  className="text-brass hover:text-brass-dark text-xs uppercase tracking-wide"
                >
                  Remove
                </button>
              </li>
            ))}
            {blocks.length === 0 && (
              <li className="text-ink-muted text-sm">No blocked dates.</li>
            )}
          </ul>
        </div>

        {/* Bookings */}
        <div>
          <h2 className="font-serif text-2xl text-ink mb-4">Booking requests</h2>

          <div className="flex flex-wrap gap-2 mb-5">
            {tabs.map((tabName) => (
              <button
                key={tabName}
                onClick={() => setFilter(tabName)}
                className={`px-4 py-1.5 text-[11px] uppercase tracking-[0.15em] border transition-colors ${
                  filter === tabName
                    ? "bg-ink text-cream border-ink"
                    : "text-ink-muted border-ink/15 hover:border-brass"
                }`}
              >
                {tabName}
              </button>
            ))}
          </div>

          <ul className="space-y-3">
            {filtered.map((b) => (
              <li key={b.id} className="border border-ink/10 bg-white p-4">
                <div className="flex items-center justify-between">
                  <span className="font-serif text-lg text-ink">
                    {b.guest_name}
                  </span>
                  <span
                    className={`text-[11px] uppercase tracking-wide ${
                      b.status === "confirmed"
                        ? "text-olive"
                        : b.status === "cancelled"
                        ? "text-red-600"
                        : "text-brass"
                    }`}
                  >
                    {b.status}
                  </span>
                </div>
                <p className="text-sm text-ink-soft mt-1">
                  {b.room_name} · {b.check_in} → {b.check_out} · {b.guests} guests
                </p>
                <p className="text-xs text-ink-muted mt-1">
                  {b.guest_email}
                  {b.guest_phone ? ` · ${b.guest_phone}` : ""}
                </p>
                {b.requests && (
                  <p className="text-xs text-ink-muted mt-1 italic">
                    {b.requests}
                  </p>
                )}
                {b.status === "pending" && (
                  <div className="flex gap-3 mt-3">
                    <button
                      onClick={() => confirmBooking(b)}
                      className="bg-ink hover:bg-brass text-cream px-4 py-2 text-[11px] uppercase tracking-wide transition-colors"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() =>
                        updateBookingStatus(b.id, { status: "cancelled" }).then(
                          refresh
                        )
                      }
                      className="border border-ink/20 text-ink-soft hover:border-red-400 px-4 py-2 text-[11px] uppercase tracking-wide transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="text-ink-muted text-sm">No bookings here.</li>
            )}
          </ul>
        </div>
      </div>
    </Wrap>
  );
}
